import React, { useMemo, useState } from 'react';
import { HandCoins } from 'lucide-react';
import { useHandLoanRecords } from '../../lib/workspaceStore';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';

function parsePrincipal(data: Record<string, unknown>) {
  const p = parseFloat(String(data.loanAmount ?? '').replace(/,/g, ''));
  return Number.isFinite(p) ? p : 0;
}

export function HandLoansOverview({
  onClose,
  setActiveItem,
  initialDateRange,
}: {
  onClose: () => void;
  setActiveItem?: (item: string) => void;
  initialDateRange?: DashboardDetailDateProps['initialDateRange'];
}) {
  const hands = useHandLoanRecords();
  const dr = useDashboardDetailDates(initialDateRange);
  const [searchQuery, setSearchQuery] = useState('');

  const inPeriod = useMemo(
    () =>
      hands.filter((r) => {
        const d = r.data;
        const start = String(d.agreementDate ?? d.startDate ?? r.createdAt);
        return inRange(start, dr.range);
      }),
    [hands, dr.range]
  );

  const totalPrincipal = useMemo(() => inPeriod.reduce((s, r) => s + parsePrincipal(r.data), 0), [inPeriod]);

  const filtered = useMemo(() => {
    const n = searchQuery.trim().toLowerCase();
    if (!n) return inPeriod;
    return inPeriod.filter((r) => {
      const d = r.data;
      return [String(d.name ?? ''), String(d.loanAmount ?? ''), String(d.loanTerm ?? ''), String(d.loanType ?? '')]
        .join(' ')
        .toLowerCase()
        .includes(n);
    });
  }, [inPeriod, searchQuery]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      filtered.map((r) => {
        const d = r.data;
        return {
          Borrower: String(d.name ?? '—'),
          Mobile: String(d.mobileNumber ?? d.suretyNumber ?? d.aadharNumber ?? '—'),
          Amount: String(d.loanAmount ?? '—'),
          TermMonths: String(d.loanTerm ?? '—'),
          InterestType: String(d.loanType ?? '—'),
          StartDate: String(d.agreementDate ?? d.startDate ?? r.createdAt),
          Updated: new Date(r.updatedAt).toLocaleString(),
        };
      }),
    [filtered]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      title="Hand loans"
      subtitle="Hand-loan files started in the selected period."
      icon={HandCoins}
      iconClassName="text-teal-700"
      exportTitle="Hand Loans Overview"
      exportFilename="hand-loans-overview.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
      headerExtra={
        setActiveItem ? (
          <button
            type="button"
            onClick={() => {
              setActiveItem('loans');
              onClose();
            }}
            className="app-btn-primary text-sm"
          >
            Open Hand loans
          </button>
        ) : undefined
      }
    >
      <DashboardMetricCard label="Principal in period" value={`₹${totalPrincipal.toLocaleString('en-IN')}`} accent />

      <PageSearchBar
        id="dash-handloan-search"
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search borrower, amount, type…"
        className="max-w-md"
      />

      {filtered.length === 0 ? (
        <div className="app-card shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="app-card-inner flex flex-col items-center gap-2 py-10 text-center">
            <HandCoins className="h-10 w-10 text-zinc-300" aria-hidden />
            <p className="font-medium text-zinc-800">No hand loans in this period</p>
            <p className="text-sm text-zinc-600">
              {inPeriod.length === 0 && hands.length > 0
                ? 'Try widening the date range.'
                : 'Create records under Hand loans in the sidebar.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
          <div className="border-b border-zinc-200 bg-zinc-50/90 px-4 py-3">
            <h2 className="text-sm font-semibold text-zinc-900">Hand loan records</h2>
            <p className="text-xs text-zinc-500">{filtered.length} row{filtered.length === 1 ? '' : 's'}</p>
          </div>
          <div className="app-table-wrap border-0">
            <table className="app-table min-w-full">
              <thead>
                <tr>
                  <th>Borrower</th>
                  <th>Mobile</th>
                  <th>Amount</th>
                  <th>Term</th>
                  <th>Type</th>
                  <th>Start</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((r) => {
                  const d = r.data;
                  return (
                    <tr key={r.id} className="transition-colors hover:bg-teal-50/40">
                      <td className="font-medium text-zinc-900">{String(d.name ?? '—')}</td>
                      <td className="font-mono text-xs text-zinc-600">
                        {String(d.mobileNumber ?? d.suretyNumber ?? d.aadharNumber ?? '—')}
                      </td>
                      <td className="tabular-nums text-zinc-800">{String(d.loanAmount ?? '—')}</td>
                      <td className="text-zinc-700">{String(d.loanTerm ?? '—')} mo</td>
                      <td className="text-zinc-700">{String(d.loanType ?? '—')}</td>
                      <td className="text-xs tabular-nums text-zinc-500">
                        {String(d.agreementDate ?? d.startDate ?? r.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardDetailShell>
  );
}
